import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalViewer from './JournalViewer';
import './PatientDetail.css';

const SECTION_CONFIG = [
    { key: 'selfAssessment', label: 'Self Assessment', icon: '📋' },
    { key: 'reflection', label: 'Reflection', icon: '💭' },
    { key: 'transformation', label: 'Transformation', icon: '🦋' },
    { key: 'checkIn01', label: 'Check-in #01', icon: '📝' },
    { key: 'checkIn02', label: 'Check-in #02', icon: '📅' },
    { key: 'checkIn03', label: 'Check-in #03', icon: '📅' },
    { key: 'checkIn04', label: 'Check-in #04', icon: '📅' },
    { key: 'checkIn05', label: 'Check-in #05', icon: '📅' },
    { key: 'checkIn06', label: 'Check-in #06', icon: '📅' }
];

const PatientDetail = ({ patient, onBack, onRefresh }) => {
    const [activeTab, setActiveTab] = useState('cycles');
    const [cycles, setCycles] = useState([]);
    const [expandedCycle, setExpandedCycle] = useState(null);
    const [cycleJournalData, setCycleJournalData] = useState({});
    const [cycleLoading, setCycleLoading] = useState({});
    const [loading, setLoading] = useState(true);

    // Consent state
    const [consentData, setConsentData] = useState([]);
    const [consentLoading, setConsentLoading] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState(null);

    useEffect(() => {
        loadCycles();
    }, [patient.id]);

    const loadCycles = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/patients/${patient.id}/cycles`);
            const cycleList = response.data.cycles || [];
            setCycles(cycleList);

            // Auto-expand the active cycle (or latest)
            if (cycleList.length > 0) {
                const activeCycle = cycleList.find(c => c.status === 'active');
                const defaultNum = activeCycle?.cycleNumber || cycleList[cycleList.length - 1].cycleNumber;
                setExpandedCycle(defaultNum);
                loadCycleJournal(defaultNum);
            }
        } catch (error) {
            console.error('Error loading cycles:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCycleJournal = async (cycleNumber) => {
        if (cycleJournalData[cycleNumber]) return;
        try {
            setCycleLoading(prev => ({ ...prev, [cycleNumber]: true }));
            const response = await api.get(`/admin/patients/${patient.id}/journal?cycle=${cycleNumber}`);
            setCycleJournalData(prev => ({ ...prev, [cycleNumber]: response.data }));
        } catch (error) {
            console.error(`Error loading journal for cycle ${cycleNumber}:`, error);
        } finally {
            setCycleLoading(prev => ({ ...prev, [cycleNumber]: false }));
        }
    };

    const toggleCycle = (cycleNumber) => {
        if (expandedCycle === cycleNumber) {
            setExpandedCycle(null);
        } else {
            setExpandedCycle(cycleNumber);
            loadCycleJournal(cycleNumber);
        }
    };

    const loadConsentForms = async () => {
        try {
            setConsentLoading(true);
            const response = await api.get(`/admin/patients/${patient.id}/consents`);
            setConsentData(response.data.consents || []);
        } catch (error) {
            console.error('Error loading consent forms:', error);
        } finally {
            setConsentLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        });
    };

    const formatDateLong = (dateString) => {
        if (!dateString) return 'Not provided';
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getCompletedSectionsForData = (journalData) => {
        if (!journalData) return [];
        return SECTION_CONFIG.map(s => s.key).filter(key => {
            const d = journalData[key];
            if (!d) return false;
            return Object.keys(d).filter(k =>
                k !== 'lastModified' && k !== '_originalSection' && k !== 'id' && k !== 'cycle' &&
                d[k] !== null && d[k] !== undefined && d[k] !== ''
            ).length > 0;
        });
    };

    const totalCompletedCycles = cycles.filter(c => c.status === 'completed').length;

    if (loading) {
        return (
            <div className="patient-detail-loading">
                <div className="loading-spinner"></div>
                <p>Loading patient data...</p>
            </div>
        );
    }

    return (
        <div className="patient-detail">
            {/* Patient Header */}
            <div className="patient-detail-header">
                <div className="patient-header-info">
                    <div className="patient-avatar-large">
                        {patient.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="patient-header-text">
                        <h1>{patient.name}</h1>
                        <p className="patient-email-large">{patient.email}</p>
                        <div className="patient-meta">
                            <span>Joined: {formatDate(patient.createdAt)}</span>
                            <span>Last Activity: {formatDate(patient.lastJournalEntry)}</span>
                        </div>
                    </div>
                </div>
                <div className="patient-cycle-summary">
                    <div className="summary-stat">
                        <span className="summary-number">{cycles.length}</span>
                        <span className="summary-label">Total Cycles</span>
                    </div>
                    <div className="summary-stat">
                        <span className="summary-number">{totalCompletedCycles}</span>
                        <span className="summary-label">Visited</span>
                    </div>
                    <div className="summary-stat">
                        <span className="summary-number">{patient.totalEntries || 0}</span>
                        <span className="summary-label">Total Entries</span>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="patient-detail-nav">
                <button
                    className={`detail-nav-tab ${activeTab === 'cycles' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cycles')}
                >
                    🔄 Cycles & Journal
                </button>
                <button
                    className={`detail-nav-tab ${activeTab === 'consent' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('consent');
                        if (consentData.length === 0) loadConsentForms();
                    }}
                >
                    📄 Consent Forms
                </button>
            </div>

            {/* Content */}
            <div className="patient-detail-content">
                {activeTab === 'cycles' && (
                    <div className="cycles-section">
                        {cycles.length === 0 ? (
                            <div className="no-cycles">
                                <span className="no-cycles-icon">📭</span>
                                <h3>No Cycles Yet</h3>
                                <p>This patient has not started any journal cycles.</p>
                            </div>
                        ) : (
                            <div className="cycles-accordion">
                                {[...cycles].reverse().map(cycle => {
                                    const isExpanded = expandedCycle === cycle.cycleNumber;
                                    const journalData = cycleJournalData[cycle.cycleNumber];
                                    const isLoadingJournal = cycleLoading[cycle.cycleNumber];
                                    const completedSections = journalData
                                        ? getCompletedSectionsForData(journalData)
                                        : [];
                                    const sectionCount = cycle.completedSections?.length || 0;
                                    const progressPercent = (sectionCount / 9) * 100;

                                    return (
                                        <div
                                            key={cycle.cycleNumber}
                                            className={`cycle-accordion-item ${isExpanded ? 'expanded' : ''} ${cycle.status}`}
                                        >
                                            {/* Cycle Header */}
                                            <div className="cycle-accordion-header" onClick={() => toggleCycle(cycle.cycleNumber)}>
                                                <div className="cycle-header-left">
                                                    <span className={`cycle-status-badge ${cycle.status}`}>
                                                        {cycle.status === 'completed' ? '✅' : '🔄'}
                                                    </span>
                                                    <div className="cycle-header-title">
                                                        <h3>Cycle {cycle.cycleNumber}</h3>
                                                        <span className="cycle-header-dates">
                                                            {formatDate(cycle.startedAt)}
                                                            {cycle.completedAt ? ` — ${formatDate(cycle.completedAt)}` : ' — In Progress'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="cycle-header-right">
                                                    <div className="cycle-header-progress">
                                                        <div className="cycle-mini-bar">
                                                            <div
                                                                className={`cycle-mini-fill ${cycle.status === 'completed' ? 'complete' : ''}`}
                                                                style={{ width: `${progressPercent}%` }}
                                                            />
                                                        </div>
                                                        <span className="cycle-progress-text">{sectionCount}/9</span>
                                                    </div>
                                                    <span className={`cycle-expand-icon ${isExpanded ? 'rotated' : ''}`}>▾</span>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="cycle-accordion-body">
                                                    {isLoadingJournal ? (
                                                        <div className="cycle-body-loading">
                                                            <div className="loading-spinner"></div>
                                                            <p>Loading journal data...</p>
                                                        </div>
                                                    ) : !journalData ? (
                                                        <div className="cycle-body-loading">
                                                            <p>No journal data available.</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Section chips showing progress at a glance */}
                                                            <div className="cycle-sections-grid">
                                                                {SECTION_CONFIG.map(section => {
                                                                    const hasData = completedSections.includes(section.key);
                                                                    const sectionData = journalData[section.key];
                                                                    const lastModified = sectionData?.lastModified;
                                                                    return (
                                                                        <div key={section.key} className={`cycle-section-chip ${hasData ? 'filled' : 'empty'}`}>
                                                                            <span className="chip-icon">{section.icon}</span>
                                                                            <span className="chip-label">{section.label}</span>
                                                                            {hasData ? (
                                                                                <span className="chip-check">✓</span>
                                                                            ) : (
                                                                                <span className="chip-pending">—</span>
                                                                            )}
                                                                            {lastModified && (
                                                                                <span className="chip-date">{formatDate(lastModified)}</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>

                                                            {/* Journal Viewer for this cycle */}
                                                            <JournalViewer
                                                                journalData={journalData}
                                                                patient={patient}
                                                                cycleNumber={cycle.cycleNumber}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'consent' && (
                    <div className="consent-section">
                        {consentLoading ? (
                            <div className="consent-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading consent forms...</p>
                            </div>
                        ) : consentData.length === 0 ? (
                            <div className="no-consent">
                                <p>No consent forms found for this patient.</p>
                            </div>
                        ) : (
                            <div className="consent-list">
                                {consentData.map((consent) => (
                                    <div key={consent.id} className="consent-card">
                                        <div className="consent-header">
                                            <h3>Journal Sharing Consent</h3>
                                            <span className={`consent-status ${consent.isActive ? 'active' : 'revoked'}`}>
                                                {consent.isActive ? '✓ Active' : '✗ Revoked'}
                                            </span>
                                        </div>
                                        <div className="consent-details">
                                            <div className="consent-detail-row">
                                                <span className="detail-label">Signed By:</span>
                                                <span className="detail-value">{consent.fullName}</span>
                                            </div>
                                            <div className="consent-detail-row">
                                                <span className="detail-label">Signed On:</span>
                                                <span className="detail-value">{formatDateLong(consent.signedAt)}</span>
                                            </div>
                                            {consent.revokedAt && (
                                                <div className="consent-detail-row">
                                                    <span className="detail-label">Revoked On:</span>
                                                    <span className="detail-value">{formatDateLong(consent.revokedAt)}</span>
                                                </div>
                                            )}
                                            {consent.revokedReason && (
                                                <div className="consent-detail-row">
                                                    <span className="detail-label">Revocation Reason:</span>
                                                    <span className="detail-value">{consent.revokedReason}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="consent-actions">
                                            <button
                                                className="btn-view-consent"
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    try {
                                                        const response = await api.get(`/consent/download/${consent.id}`);
                                                        setSelectedConsent(response.data.consent);
                                                        setShowConsentModal(true);
                                                    } catch (error) {
                                                        console.error('Error viewing consent:', error);
                                                        alert('Failed to load consent details');
                                                    }
                                                }}
                                            >
                                                📄 View Full Consent
                                            </button>
                                            <button
                                                className="btn-download-pdf"
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    try {
                                                        const response = await api.get(`/admin/patients/${patient.id}/consents/${consent.id}/pdf`, {
                                                            responseType: 'blob'
                                                        });
                                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', `consent-${consent.fullName}-${new Date(consent.signedAt).toLocaleDateString()}.pdf`);
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        link.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        console.error('Error downloading PDF:', error);
                                                        alert('Failed to download consent PDF');
                                                    }
                                                }}
                                            >
                                                ⬇️ Download PDF
                                            </button>
                                        </div>
                                        {consent.signature && (
                                            <div className="consent-signature">
                                                <span className="signature-label">Signature:</span>
                                                <img src={consent.signature} alt="Patient signature" className="signature-image" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Consent Modal */}
            {showConsentModal && selectedConsent && (
                <div className="consent-modal-overlay" onClick={() => setShowConsentModal(false)}>
                    <div className="consent-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="consent-modal-header">
                            <h2>Consent Form Details</h2>
                            <button className="modal-close-btn" onClick={() => setShowConsentModal(false)}>✕</button>
                        </div>
                        <div className="consent-modal-content">
                            <div className="consent-modal-section">
                                <div className="consent-modal-status">
                                    <span className={`consent-badge ${selectedConsent.isActive ? 'active' : 'revoked'}`}>
                                        {selectedConsent.isActive ? '✓ Active' : '✗ Revoked'}
                                    </span>
                                </div>
                            </div>
                            <div className="consent-modal-section">
                                <h3>Signer Information</h3>
                                <div className="modal-info-grid">
                                    <div className="modal-info-item">
                                        <span className="modal-label">Full Name:</span>
                                        <span className="modal-value">{selectedConsent.fullName}</span>
                                    </div>
                                    <div className="modal-info-item">
                                        <span className="modal-label">Email:</span>
                                        <span className="modal-value">{selectedConsent.userEmail}</span>
                                    </div>
                                    <div className="modal-info-item">
                                        <span className="modal-label">Signed On:</span>
                                        <span className="modal-value">{formatDateLong(selectedConsent.signedAt)}</span>
                                    </div>
                                    {selectedConsent.revokedAt && (
                                        <>
                                            <div className="modal-info-item">
                                                <span className="modal-label">Revoked On:</span>
                                                <span className="modal-value">{formatDateLong(selectedConsent.revokedAt)}</span>
                                            </div>
                                            {selectedConsent.revokedReason && (
                                                <div className="modal-info-item full-width">
                                                    <span className="modal-label">Revocation Reason:</span>
                                                    <span className="modal-value">{selectedConsent.revokedReason}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="consent-modal-section">
                                <h3>Consent Text</h3>
                                <div className="consent-text-container">
                                    {selectedConsent.consentText}
                                </div>
                            </div>
                            {selectedConsent.signature && (
                                <div className="consent-modal-section">
                                    <h3>Signature</h3>
                                    <div className="modal-signature-container">
                                        <img src={selectedConsent.signature} alt="Signature" className="modal-signature-image" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="consent-modal-footer">
                            <button className="btn-modal-close" onClick={() => setShowConsentModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientDetail;
